// frontend/src/services/cloudinary.js
import api from "./api";

export const uploadFile = async (file, type = "image", options = {}) => {
  const formData = new FormData();
  formData.append("file", file);

  Object.keys(options).forEach((key) => {
    formData.append(key, options[key]);
  });

  const endpoint =
    type === "video"
      ? "/upload/video"
      : type === "thumbnail"
        ? "/upload/thumbnail"
        : "/upload/image";

  try {
    console.log(
      `📤 Uploading ${type}: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`,
    );

    const response = await api.post(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 120000, // 120 second timeout
      onUploadProgress: (progressEvent) => {
        if (options.onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || file.size),
          );
          options.onProgress(Math.min(percentCompleted, 100));
        }
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.error || "Upload failed");
    }

    console.log(`✅ ${type} uploaded successfully`);
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    console.error(`❌ ${type} upload error:`, error);

    let errorMessage = error.message || "Upload failed";

    if (error.response?.status === 413) {
      errorMessage =
        "File is too large. Please compress your file and try again.";
    } else if (error.response?.status === 403) {
      errorMessage =
        "Permission denied. Please check your Cloudinary credentials.";
    } else if (
      error.code === "ECONNABORTED" ||
      error.message.includes("timeout")
    ) {
      errorMessage =
        "Upload timed out. Your internet connection may be slow or the file is too large.";
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Upload a video
 */
export const uploadVideoFile = async (file, courseId, moduleId, onProgress) => {
  return uploadFile(file, "video", {
    courseId,
    publicId: `module_${moduleId}_${Date.now()}`,
    onProgress,
  });
};

/**
 * Upload an image
 */
export const uploadImageFile = async (file, folder, onProgress) => {
  return uploadFile(file, "image", {
    folder,
    publicId: `${Date.now()}_${file.name}`,
    onProgress,
  });
};

/**
 * Upload a thumbnail
 */
export const uploadThumbnailFile = async (file, onProgress) => {
  return uploadFile(file, "thumbnail", {
    publicId: `thumbnail_${Date.now()}`,
    onProgress,
  });
};

/**
 * Delete a file from Cloudinary
 */
export const deleteFile = async (publicId, resourceType = "image") => {
  try {
    await api.delete(`/upload/${publicId}?resourceType=${resourceType}`);
    return { success: true };
  } catch (error) {
    console.error("Delete error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
